// VRP UI Context - Unified UI State Management
import React, { createContext, useContext, useReducer, useMemo, useCallback, ReactNode } from 'react';
import type {
  VRPUIContextType,
  VRPUIState,
  VRPUIActions,
  FeatureUIState,
  VRPNotification,
  BulkActionType,
  ColumnConfig
} from '../types/ui';
import type { EntityTypeName } from '../types/data';
import type { VRPFeature } from '../types/toolpad';
import type { ViewMode } from '../types/ui';

// Initial state factory
const createInitialFeatureState = (): FeatureUIState => ({
  viewMode: 'grid',
  sortBy: 'name',
  sortDirection: 'asc',
  filters: {},
  searchQuery: '',
  pagination: {
    page: 1,
    limit: 20,
    total: 0
  },
  columns: [],
  expandedRows: new Set(),
  selectedTab: 0
});

const createInitialState = (): VRPUIState => ({
  theme: 'light',
  sidebarCollapsed: false,
  fullScreenMode: false,
  modals: {
    createEntity: {
      isOpen: false,
      entityType: null,
      initialData: undefined
    },
    editEntity: {
      isOpen: false,
      entityType: null,
      entityId: null,
      data: undefined
    },
    deleteConfirmation: {
      isOpen: false,
      entityType: null,
      entityId: null,
      entityName: undefined
    },
    bulkActions: {
      isOpen: false,
      action: null,
      selectedIds: new Set(),
      entityType: null
    },
    custom: {}
  },
  selections: {
    vehicles: new Set(),
    locations: new Set(),
    jobs: new Set(),
    products: new Set(),
    shipments: new Set(),
    skills: new Set(),
    scenarios: new Set(),
    datasets: new Set(),
    optimizations: new Set(),
    routes: new Set()
  },
  features: {
    dashboard: createInitialFeatureState(),
    vehicles: createInitialFeatureState(),
    'enhanced-vehicles': createInitialFeatureState(),
    locations: createInitialFeatureState(),
    jobs: createInitialFeatureState(),
    products: createInitialFeatureState(),
    shipments: createInitialFeatureState(),
    skills: createInitialFeatureState(),
    optimization: createInitialFeatureState(),
    'optimization-results': createInitialFeatureState(),
    map: createInitialFeatureState(),
    schedule: createInitialFeatureState(),
    scenarios: createInitialFeatureState()
  },
  notifications: [],
  loadingOverlays: {
    global: false
  }
});

// Action types
type UIAction =
  | { type: 'SET_THEME'; payload: 'light' | 'dark' | 'auto' }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_FULLSCREEN'; payload: boolean }
  | { type: 'OPEN_MODAL'; payload: { modalType: string; data?: Record<string, any> } }
  | { type: 'CLOSE_MODAL'; payload: string }
  | { type: 'CLOSE_ALL_MODALS' }
  | { type: 'SELECT_ITEM'; payload: { entityType: EntityTypeName; id: number } }
  | { type: 'DESELECT_ITEM'; payload: { entityType: EntityTypeName; id: number } }
  | { type: 'SELECT_ALL'; payload: { entityType: EntityTypeName; ids: number[] } }
  | { type: 'CLEAR_SELECTION'; payload: EntityTypeName }
  | { type: 'TOGGLE_SELECTION'; payload: { entityType: EntityTypeName; id: number } }
  | { type: 'SET_VIEW_MODE'; payload: { feature: VRPFeature; mode: ViewMode } }
  | { type: 'SET_SORTING'; payload: { feature: VRPFeature; sortBy: string; direction: 'asc' | 'desc' } }
  | { type: 'SET_FILTERS'; payload: { feature: VRPFeature; filters: Record<string, any> } }
  | { type: 'SET_SEARCH_QUERY'; payload: { feature: VRPFeature; query: string } }
  | { type: 'SET_PAGINATION'; payload: { feature: VRPFeature; page: number; limit: number } }
  | { type: 'UPDATE_COLUMNS'; payload: { feature: VRPFeature; columns: ColumnConfig[] } }
  | { type: 'TOGGLE_ROW_EXPANSION'; payload: { feature: VRPFeature; id: number } }
  | { type: 'SET_SELECTED_TAB'; payload: { feature: VRPFeature; tab: number } }
  | { type: 'SHOW_NOTIFICATION'; payload: VRPNotification }
  | { type: 'DISMISS_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_ALL_NOTIFICATIONS' }
  | { type: 'SET_GLOBAL_LOADING'; payload: boolean }
  | { type: 'SET_LOADING_OVERLAY'; payload: { key: string; loading: boolean } }
  | { type: 'CLEAR_ALL_LOADING_OVERLAYS' }
  | { type: 'LOAD_UI_STATE'; payload: Partial<VRPUIState> }
  | { type: 'RESET_UI_STATE' };

// Reducer
const uiReducer = (state: VRPUIState, action: UIAction): VRPUIState => {
  switch (action.type) {
    case 'SET_THEME':
      return { ...state, theme: action.payload };

    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };

    case 'SET_FULLSCREEN':
      return { ...state, fullScreenMode: action.payload };

    case 'OPEN_MODAL': {
      const { modalType, data } = action.payload;
      
      if (modalType.startsWith('create-')) {
        const entityType = modalType.replace('create-', '') as EntityTypeName;
        return {
          ...state,
          modals: {
            ...state.modals,
            createEntity: {
              isOpen: true,
              entityType,
              initialData: data
            }
          }
        };
      }
      
      if (modalType.startsWith('edit-')) {
        const entityType = modalType.replace('edit-', '') as EntityTypeName;
        return {
          ...state,
          modals: {
            ...state.modals,
            editEntity: {
              isOpen: true,
              entityType,
              entityId: data?.id || null,
              data
            }
          }
        };
      }
      
      if (modalType.startsWith('delete-')) {
        const entityType = modalType.replace('delete-', '') as EntityTypeName;
        return {
          ...state,
          modals: {
            ...state.modals,
            deleteConfirmation: {
              isOpen: true,
              entityType,
              entityId: data?.id || null,
              entityName: data?.name
            }
          }
        };
      }
      
      if (modalType.startsWith('bulk-')) {
        const action = modalType.replace('bulk-', '') as BulkActionType;
        return {
          ...state,
          modals: {
            ...state.modals,
            bulkActions: {
              isOpen: true,
              action,
              selectedIds: new Set(data?.selectedIds || []),
              entityType: data?.entityType || null
            }
          }
        };
      }
      
      // Custom modal
      return {
        ...state,
        modals: {
          ...state.modals,
          custom: {
            ...state.modals.custom,
            [modalType]: {
              isOpen: true,
              data
            }
          }
        }
      };
    }

    case 'CLOSE_MODAL': {
      const modalType = action.payload;
      
      if (modalType.startsWith('create-')) {
        return {
          ...state,
          modals: {
            ...state.modals,
            createEntity: {
              isOpen: false,
              entityType: null,
              initialData: undefined
            }
          }
        };
      }
      
      if (modalType.startsWith('edit-')) {
        return {
          ...state,
          modals: {
            ...state.modals,
            editEntity: {
              isOpen: false,
              entityType: null,
              entityId: null,
              data: undefined
            }
          }
        };
      }
      
      if (modalType.startsWith('delete-')) {
        return {
          ...state,
          modals: {
            ...state.modals,
            deleteConfirmation: {
              isOpen: false,
              entityType: null,
              entityId: null,
              entityName: undefined
            }
          }
        };
      }
      
      if (modalType.startsWith('bulk-')) {
        return {
          ...state,
          modals: {
            ...state.modals,
            bulkActions: {
              isOpen: false,
              action: null,
              selectedIds: new Set(),
              entityType: null
            }
          }
        };
      }
      
      // Custom modal
      const customModals = { ...state.modals.custom };
      if (customModals[modalType]) {
        customModals[modalType] = {
          ...customModals[modalType],
          isOpen: false
        };
      }
      
      return {
        ...state,
        modals: {
          ...state.modals,
          custom: customModals
        }
      };
    }

    case 'CLOSE_ALL_MODALS':
      return {
        ...state,
        modals: {
          createEntity: { isOpen: false, entityType: null },
          editEntity: { isOpen: false, entityType: null, entityId: null },
          deleteConfirmation: { isOpen: false, entityType: null, entityId: null },
          bulkActions: { isOpen: false, action: null, selectedIds: new Set(), entityType: null },
          custom: Object.keys(state.modals.custom).reduce((acc, key) => ({
            ...acc,
            [key]: { ...state.modals.custom[key], isOpen: false }
          }), {})
        }
      };

    case 'SELECT_ITEM': {
      const { entityType, id } = action.payload;
      const newSelections = new Set(state.selections[entityType]);
      newSelections.add(id);
      return {
        ...state,
        selections: {
          ...state.selections,
          [entityType]: newSelections
        }
      };
    }

    case 'DESELECT_ITEM': {
      const { entityType, id } = action.payload;
      const newSelections = new Set(state.selections[entityType]);
      newSelections.delete(id);
      return {
        ...state,
        selections: {
          ...state.selections,
          [entityType]: newSelections
        }
      };
    }

    case 'SELECT_ALL': {
      const { entityType, ids } = action.payload;
      return {
        ...state,
        selections: {
          ...state.selections,
          [entityType]: new Set(ids)
        }
      };
    }

    case 'CLEAR_SELECTION': {
      const entityType = action.payload;
      return {
        ...state,
        selections: {
          ...state.selections,
          [entityType]: new Set()
        }
      };
    }

    case 'TOGGLE_SELECTION': {
      const { entityType, id } = action.payload;
      const currentSelections = state.selections[entityType];
      const newSelections = new Set(currentSelections);
      
      if (newSelections.has(id)) {
        newSelections.delete(id);
      } else {
        newSelections.add(id);
      }
      
      return {
        ...state,
        selections: {
          ...state.selections,
          [entityType]: newSelections
        }
      };
    }

    case 'SET_VIEW_MODE': {
      const { feature, mode } = action.payload;
      return {
        ...state,
        features: {
          ...state.features,
          [feature]: {
            ...state.features[feature],
            viewMode: mode
          }
        }
      };
    }

    case 'SET_SORTING': {
      const { feature, sortBy, direction } = action.payload;
      return {
        ...state,
        features: {
          ...state.features,
          [feature]: {
            ...state.features[feature],
            sortBy,
            sortDirection: direction
          }
        }
      };
    }

    case 'SET_FILTERS': {
      const { feature, filters } = action.payload;
      return {
        ...state,
        features: {
          ...state.features,
          [feature]: {
            ...state.features[feature],
            filters
          }
        }
      };
    }

    case 'SET_SEARCH_QUERY': {
      const { feature, query } = action.payload;
      return {
        ...state,
        features: {
          ...state.features,
          [feature]: {
            ...state.features[feature],
            searchQuery: query
          }
        }
      };
    }

    case 'SET_PAGINATION': {
      const { feature, page, limit } = action.payload;
      return {
        ...state,
        features: {
          ...state.features,
          [feature]: {
            ...state.features[feature],
            pagination: {
              ...state.features[feature].pagination,
              page,
              limit
            }
          }
        }
      };
    }

    case 'UPDATE_COLUMNS': {
      const { feature, columns } = action.payload;
      return {
        ...state,
        features: {
          ...state.features,
          [feature]: {
            ...state.features[feature],
            columns
          }
        }
      };
    }

    case 'TOGGLE_ROW_EXPANSION': {
      const { feature, id } = action.payload;
      const expandedRows = new Set(state.features[feature].expandedRows);
      
      if (expandedRows.has(id)) {
        expandedRows.delete(id);
      } else {
        expandedRows.add(id);
      }
      
      return {
        ...state,
        features: {
          ...state.features,
          [feature]: {
            ...state.features[feature],
            expandedRows
          }
        }
      };
    }

    case 'SET_SELECTED_TAB': {
      const { feature, tab } = action.payload;
      return {
        ...state,
        features: {
          ...state.features,
          [feature]: {
            ...state.features[feature],
            selectedTab: tab
          }
        }
      };
    }

    case 'SHOW_NOTIFICATION': {
      const notification = action.payload;
      return {
        ...state,
        notifications: [...state.notifications, notification]
      };
    }

    case 'DISMISS_NOTIFICATION': {
      const notificationId = action.payload;
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== notificationId)
      };
    }

    case 'CLEAR_ALL_NOTIFICATIONS':
      return {
        ...state,
        notifications: []
      };

    case 'SET_GLOBAL_LOADING': {
      return {
        ...state,
        loadingOverlays: {
          ...state.loadingOverlays,
          global: action.payload
        }
      };
    }

    case 'SET_LOADING_OVERLAY': {
      const { key, loading } = action.payload;
      return {
        ...state,
        loadingOverlays: {
          ...state.loadingOverlays,
          [key]: loading
        }
      };
    }

    case 'CLEAR_ALL_LOADING_OVERLAYS':
      return {
        ...state,
        loadingOverlays: { global: false }
      };

    case 'LOAD_UI_STATE': {
      // Validate loaded state and merge safely with current state
      const loadedState = action.payload;
      if (!loadedState || typeof loadedState !== 'object') {
        console.warn('[VRPUIContext] Invalid loaded state, keeping current state');
        return state;
      }
      
      const mergedState = { ...state };
      
      // Safely merge selections
      if (loadedState.selections && typeof loadedState.selections === 'object') {
        mergedState.selections = { ...state.selections };
        Object.keys(state.selections).forEach(key => {
          const loadedSelection = loadedState.selections[key];
          if (loadedSelection instanceof Set) {
            mergedState.selections[key as EntityTypeName] = loadedSelection;
          } else if (Array.isArray(loadedSelection)) {
            mergedState.selections[key as EntityTypeName] = new Set(loadedSelection);
          }
          // Keep existing Set if loaded selection is invalid
        });
      }
      
      // Safely merge features
      if (loadedState.features && typeof loadedState.features === 'object') {
        mergedState.features = { ...state.features };
        Object.keys(state.features).forEach(key => {
          const loadedFeature = loadedState.features[key];
          if (loadedFeature && typeof loadedFeature === 'object') {
            mergedState.features[key as VRPFeature] = {
              ...state.features[key as VRPFeature],
              ...loadedFeature,
              // Ensure expandedRows is always a Set
              expandedRows: Array.isArray(loadedFeature.expandedRows) 
                ? new Set(loadedFeature.expandedRows)
                : loadedFeature.expandedRows instanceof Set
                ? loadedFeature.expandedRows
                : new Set()
            };
          }
        });
      }
      
      // Merge other properties safely
      Object.keys(loadedState).forEach(key => {
        if (key !== 'selections' && key !== 'features' && loadedState[key] !== undefined) {
          (mergedState as any)[key] = loadedState[key];
        }
      });
      
      return mergedState;
    }

    case 'RESET_UI_STATE':
      return createInitialState();

    default:
      return state;
  }
};

const VRPUIContext = createContext<VRPUIContextType | null>(null);

interface VRPUIProviderProps {
  children: ReactNode;
}

export const VRPUIProvider: React.FC<VRPUIProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(uiReducer, createInitialState());

  // Load persisted state on mount with comprehensive validation
  React.useEffect(() => {
    const savedState = localStorage.getItem('vrp_ui_state');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        
        // Validate parsed state structure
        if (!parsedState || typeof parsedState !== 'object') {
          console.warn('[VRPUIContext] Invalid saved state structure, resetting to defaults');
          localStorage.removeItem('vrp_ui_state');
          return;
        }

        // Create a complete valid state by merging with initial state
        const initialState = createInitialState();
        const validatedState = { ...initialState };

        // Validate and merge selections with comprehensive checks
        if (parsedState.selections && typeof parsedState.selections === 'object') {
          validatedState.selections = { ...initialState.selections };
          Object.keys(initialState.selections).forEach(entityType => {
            try {
              const savedSelection = parsedState.selections[entityType];
              if (Array.isArray(savedSelection)) {
                validatedState.selections[entityType as EntityTypeName] = new Set(savedSelection);
              } else if (savedSelection instanceof Set) {
                validatedState.selections[entityType as EntityTypeName] = savedSelection;
              }
              // If savedSelection is neither array nor Set, keep the initial empty Set
            } catch (selectionError) {
              console.warn(`[VRPUIContext] Failed to restore selection for ${entityType}:`, selectionError);
              // Keep the initial empty Set for this entity type
            }
          });
        }

        // Validate and merge features with comprehensive checks
        if (parsedState.features && typeof parsedState.features === 'object') {
          validatedState.features = { ...initialState.features };
          Object.keys(initialState.features).forEach(featureName => {
            try {
              const savedFeature = parsedState.features[featureName];
              if (savedFeature && typeof savedFeature === 'object') {
                const mergedFeature = { ...initialState.features[featureName as VRPFeature] };
                
                // Safely merge each property
                if (typeof savedFeature.viewMode === 'string') mergedFeature.viewMode = savedFeature.viewMode;
                if (typeof savedFeature.sortBy === 'string') mergedFeature.sortBy = savedFeature.sortBy;
                if (typeof savedFeature.sortDirection === 'string') mergedFeature.sortDirection = savedFeature.sortDirection;
                if (typeof savedFeature.searchQuery === 'string') mergedFeature.searchQuery = savedFeature.searchQuery;
                if (typeof savedFeature.selectedTab === 'number') mergedFeature.selectedTab = savedFeature.selectedTab;
                
                // Safely restore filters
                if (savedFeature.filters && typeof savedFeature.filters === 'object') {
                  mergedFeature.filters = { ...savedFeature.filters };
                }
                
                // Safely restore pagination
                if (savedFeature.pagination && typeof savedFeature.pagination === 'object') {
                  mergedFeature.pagination = {
                    page: typeof savedFeature.pagination.page === 'number' ? savedFeature.pagination.page : 1,
                    limit: typeof savedFeature.pagination.limit === 'number' ? savedFeature.pagination.limit : 20,
                    total: typeof savedFeature.pagination.total === 'number' ? savedFeature.pagination.total : 0
                  };
                }
                
                // Safely restore columns
                if (Array.isArray(savedFeature.columns)) {
                  mergedFeature.columns = savedFeature.columns;
                }
                
                // Safely restore expandedRows
                if (Array.isArray(savedFeature.expandedRows)) {
                  mergedFeature.expandedRows = new Set(savedFeature.expandedRows);
                } else if (savedFeature.expandedRows instanceof Set) {
                  mergedFeature.expandedRows = savedFeature.expandedRows;
                }
                
                validatedState.features[featureName as VRPFeature] = mergedFeature;
              }
            } catch (featureError) {
              console.warn(`[VRPUIContext] Failed to restore feature state for ${featureName}:`, featureError);
              // Keep the initial feature state for this feature
            }
          });
        }

        // Validate and merge other state properties safely
        if (typeof parsedState.theme === 'string') validatedState.theme = parsedState.theme;
        if (typeof parsedState.sidebarCollapsed === 'boolean') validatedState.sidebarCollapsed = parsedState.sidebarCollapsed;
        if (typeof parsedState.fullScreenMode === 'boolean') validatedState.fullScreenMode = parsedState.fullScreenMode;
        
        // Merge modals safely
        if (parsedState.modals && typeof parsedState.modals === 'object') {
          validatedState.modals = { ...initialState.modals, ...parsedState.modals };
        }
        
        // Merge notifications safely
        if (Array.isArray(parsedState.notifications)) {
          validatedState.notifications = parsedState.notifications;
        }
        
        // Merge loading overlays safely
        if (parsedState.loadingOverlays && typeof parsedState.loadingOverlays === 'object') {
          validatedState.loadingOverlays = { ...initialState.loadingOverlays, ...parsedState.loadingOverlays };
        }

        console.log('[VRPUIContext] Successfully loaded and validated UI state from localStorage');
        dispatch({ type: 'LOAD_UI_STATE', payload: validatedState });
      } catch (error) {
        console.warn('[VRPUIContext] Failed to load saved state, using defaults:', error);
        localStorage.removeItem('vrp_ui_state');
      }
    }
  }, []);

  // Actions
  const actions: VRPUIActions = useMemo(() => ({
    setTheme: useCallback((theme: 'light' | 'dark' | 'auto') => {
      dispatch({ type: 'SET_THEME', payload: theme });
    }, []),

    toggleSidebar: useCallback(() => {
      dispatch({ type: 'TOGGLE_SIDEBAR' });
    }, []),

    setFullScreen: useCallback((enabled: boolean) => {
      dispatch({ type: 'SET_FULLSCREEN', payload: enabled });
    }, []),

    openModal: useCallback((modalType: string, data?: Record<string, any>) => {
      dispatch({ type: 'OPEN_MODAL', payload: { modalType, data } });
    }, []),

    closeModal: useCallback((modalType: string) => {
      dispatch({ type: 'CLOSE_MODAL', payload: modalType });
    }, []),

    closeAllModals: useCallback(() => {
      dispatch({ type: 'CLOSE_ALL_MODALS' });
    }, []),

    selectItem: useCallback((entityType: EntityTypeName, id: number) => {
      dispatch({ type: 'SELECT_ITEM', payload: { entityType, id } });
    }, []),

    deselectItem: useCallback((entityType: EntityTypeName, id: number) => {
      dispatch({ type: 'DESELECT_ITEM', payload: { entityType, id } });
    }, []),

    selectAll: useCallback((entityType: EntityTypeName, ids: number[]) => {
      dispatch({ type: 'SELECT_ALL', payload: { entityType, ids } });
    }, []),

    clearSelection: useCallback((entityType: EntityTypeName) => {
      dispatch({ type: 'CLEAR_SELECTION', payload: entityType });
    }, []),

    toggleSelection: useCallback((entityType: EntityTypeName, id: number) => {
      dispatch({ type: 'TOGGLE_SELECTION', payload: { entityType, id } });
    }, []),

    setViewMode: useCallback((feature: VRPFeature, mode: ViewMode) => {
      dispatch({ type: 'SET_VIEW_MODE', payload: { feature, mode } });
    }, []),

    setSorting: useCallback((feature: VRPFeature, sortBy: string, direction: 'asc' | 'desc') => {
      dispatch({ type: 'SET_SORTING', payload: { feature, sortBy, direction } });
    }, []),

    setFilters: useCallback((feature: VRPFeature, filters: Record<string, any>) => {
      dispatch({ type: 'SET_FILTERS', payload: { feature, filters } });
    }, []),

    setSearchQuery: useCallback((feature: VRPFeature, query: string) => {
      dispatch({ type: 'SET_SEARCH_QUERY', payload: { feature, query } });
    }, []),

    setPagination: useCallback((feature: VRPFeature, page: number, limit: number) => {
      dispatch({ type: 'SET_PAGINATION', payload: { feature, page, limit } });
    }, []),

    updateColumns: useCallback((feature: VRPFeature, columns: ColumnConfig[]) => {
      dispatch({ type: 'UPDATE_COLUMNS', payload: { feature, columns } });
    }, []),

    toggleRowExpansion: useCallback((feature: VRPFeature, id: number) => {
      dispatch({ type: 'TOGGLE_ROW_EXPANSION', payload: { feature, id } });
    }, []),

    setSelectedTab: useCallback((feature: VRPFeature, tab: number) => {
      dispatch({ type: 'SET_SELECTED_TAB', payload: { feature, tab } });
    }, []),

    showNotification: useCallback((notification: Omit<VRPNotification, 'id' | 'timestamp'>) => {
      const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const fullNotification: VRPNotification = {
        ...notification,
        id,
        timestamp: Date.now()
      };
      
      dispatch({ type: 'SHOW_NOTIFICATION', payload: fullNotification });
      
      // Auto-dismiss if duration is set
      if (notification.duration && !notification.persistent) {
        setTimeout(() => {
          dispatch({ type: 'DISMISS_NOTIFICATION', payload: id });
        }, notification.duration);
      }
      
      return id;
    }, []),

    dismissNotification: useCallback((id: string) => {
      dispatch({ type: 'DISMISS_NOTIFICATION', payload: id });
    }, []),

    clearAllNotifications: useCallback(() => {
      dispatch({ type: 'CLEAR_ALL_NOTIFICATIONS' });
    }, []),

    setGlobalLoading: useCallback((loading: boolean) => {
      dispatch({ type: 'SET_GLOBAL_LOADING', payload: loading });
    }, []),

    setLoadingOverlay: useCallback((key: string, loading: boolean) => {
      dispatch({ type: 'SET_LOADING_OVERLAY', payload: { key, loading } });
    }, []),

    clearAllLoadingOverlays: useCallback(() => {
      dispatch({ type: 'CLEAR_ALL_LOADING_OVERLAYS' });
    }, []),

    saveUIState: useCallback(() => {
      try {
        // Enhanced defensive checks: ensure state is properly initialized before saving
        if (!state || typeof state !== 'object') {
          console.warn('[VRPUIContext] State is not a valid object, skipping save');
          return;
        }

        if (!state.selections || typeof state.selections !== 'object') {
          console.warn('[VRPUIContext] State.selections is not a valid object, skipping save');
          return;
        }

        if (!state.features || typeof state.features !== 'object') {
          console.warn('[VRPUIContext] State.features is not a valid object, skipping save');
          return;
        }

        // Convert Sets to arrays for JSON serialization with comprehensive defensive checks
        const stateToSave = {
          ...state,
          selections: (() => {
            try {
              const keys = Object.keys(state.selections);
              if (!Array.isArray(keys) || keys.length === 0) {
                console.warn('[VRPUIContext] No selection keys found, using empty object');
                return {};
              }
              
              return keys.reduce((acc, key) => {
                try {
                  const selection = state.selections[key as EntityTypeName];
                  // Enhanced validation - ensure selection is iterable before Array.from
                  if (selection instanceof Set) {
                    return {
                      ...acc,
                      [key]: Array.from(selection)
                    };
                  } else if (Array.isArray(selection)) {
                    return {
                      ...acc,
                      [key]: selection
                    };
                  } else if (selection && typeof selection === 'object' && selection[Symbol.iterator]) {
                    // Handle other iterable objects
                    return {
                      ...acc,
                      [key]: Array.from(selection)
                    };
                  } else {
                    // Default to empty array for invalid selections
                    return {
                      ...acc,
                      [key]: []
                    };
                  }
                } catch (keyError) {
                  console.warn(`[VRPUIContext] Failed to process selection key ${key}:`, keyError);
                  return {
                    ...acc,
                    [key]: []
                  };
                }
              }, {});
            } catch (selectionsError) {
              console.warn('[VRPUIContext] Failed to process selections:', selectionsError);
              return {};
            }
          })(),
          features: (() => {
            try {
              const keys = Object.keys(state.features);
              if (!Array.isArray(keys) || keys.length === 0) {
                console.warn('[VRPUIContext] No feature keys found, using empty object');
                return {};
              }
              
              return keys.reduce((acc, feature) => {
                try {
                  const featureState = state.features[feature as VRPFeature];
                  if (!featureState || typeof featureState !== 'object') {
                    console.warn(`[VRPUIContext] Invalid feature state for ${feature}, skipping`);
                    return acc;
                  }
                  
                  // Enhanced defensive checks for expandedRows with multiple validation layers
                  const expandedRows = featureState.expandedRows;
                  let safeExpandedRows: (string | number)[] = [];
                  
                  try {
                    if (expandedRows instanceof Set) {
                      safeExpandedRows = Array.from(expandedRows);
                    } else if (Array.isArray(expandedRows)) {
                      safeExpandedRows = expandedRows;
                    } else if (expandedRows && typeof expandedRows === 'object') {
                      // Try to handle other iterable objects
                      if (expandedRows[Symbol.iterator]) {
                        safeExpandedRows = Array.from(expandedRows);
                      } else {
                        console.warn(`[VRPUIContext] expandedRows for ${feature} is not iterable, using empty array`);
                        safeExpandedRows = [];
                      }
                    } else if (expandedRows !== undefined && expandedRows !== null) {
                      console.warn(`[VRPUIContext] Unexpected expandedRows type for ${feature}:`, typeof expandedRows);
                      safeExpandedRows = [];
                    }
                  } catch (expandedRowsError) {
                    console.warn(`[VRPUIContext] Error processing expandedRows for ${feature}:`, expandedRowsError);
                    safeExpandedRows = [];
                  }
                    
                  return {
                    ...acc,
                    [feature]: {
                      ...featureState,
                      expandedRows: safeExpandedRows
                    }
                  };
                } catch (featureError) {
                  console.warn(`[VRPUIContext] Failed to process feature ${feature}:`, featureError);
                  return acc;
                }
              }, {});
            } catch (featuresError) {
              console.warn('[VRPUIContext] Failed to process features:', featuresError);
              return {};
            }
          })()
        };
        
        // Final validation before saving
        if (!stateToSave.selections || !stateToSave.features) {
          console.warn('[VRPUIContext] Invalid state structure after processing, skipping save');
          return;
        }
        
        localStorage.setItem('vrp_ui_state', JSON.stringify(stateToSave));
      } catch (error) {
        console.error('[VRPUIContext] Critical error in saveUIState:', error);
      }
    }, [state]),

    loadUIState: useCallback(() => {
      const savedState = localStorage.getItem('vrp_ui_state');
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          dispatch({ type: 'LOAD_UI_STATE', payload: parsedState });
        } catch (error) {
          console.warn('[VRPUIContext] Failed to load saved state:', error);
        }
      }
    }, []),

    resetUIState: useCallback(() => {
      dispatch({ type: 'RESET_UI_STATE' });
      localStorage.removeItem('vrp_ui_state');
    }, [])
  }), [state]);

  // Auto-save state changes with comprehensive validation and proper cleanup
  React.useEffect(() => {
    // Early return if actions not available yet
    if (!actions) {
      return;
    }
    
    let timeoutId: NodeJS.Timeout;
    let isMounted = true;
    
    // Comprehensive state validation before attempting to save
    const isStateValid = () => {
      if (!state || typeof state !== 'object') {
        return false;
      }
      
      // Check if selections object exists and has the required structure
      if (!state.selections || typeof state.selections !== 'object') {
        return false;
      }
      
      // Check if features object exists and has the required structure
      if (!state.features || typeof state.features !== 'object') {
        return false;
      }
      
      // Validate that selections contains Set objects
      try {
        const selectionKeys = Object.keys(state.selections);
        for (const key of selectionKeys) {
          const selection = state.selections[key as EntityTypeName];
          if (selection && !(selection instanceof Set)) {
            return false;
          }
        }
      } catch (error) {
        console.warn('[VRPUIContext] Selection validation failed:', error);
        return false;
      }
      
      // Validate that features contains proper structure
      try {
        const featureKeys = Object.keys(state.features);
        for (const key of featureKeys) {
          const feature = state.features[key as VRPFeature];
          if (!feature || typeof feature !== 'object') {
            return false;
          }
          // Check if expandedRows exists and is a Set
          if (feature.expandedRows && !(feature.expandedRows instanceof Set)) {
            return false;
          }
        }
      } catch (error) {
        console.warn('[VRPUIContext] Feature validation failed:', error);
        return false;
      }
      
      return true;
    };
    
    // Only save if component is mounted, state is valid, and actions are available
    if (isMounted && isStateValid() && actions && typeof actions.saveUIState === 'function') {
      timeoutId = setTimeout(() => {
        if (isMounted && isStateValid()) {
          try {
            actions.saveUIState();
          } catch (error) {
            console.error('[VRPUIContext] Failed to save UI state:', error);
          }
        }
      }, 1000);
    }
    
    // Cleanup function to prevent memory leaks and state updates on unmounted components
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [state]); // Only depend on state, actions is derived from state

  const value: VRPUIContextType = useMemo(() => ({
    state,
    actions
  }), [state]); // Only depend on state, actions is derived from state

  return (
    <VRPUIContext.Provider value={value}>
      {children}
    </VRPUIContext.Provider>
  );
};

export const useVRPUI = (): VRPUIContextType => {
  const context = useContext(VRPUIContext);
  if (!context) {
    throw new Error('useVRPUI must be used within a VRPUIProvider');
  }
  return context;
};

// Convenience hooks
export const useEntitySelection = (entityType: EntityTypeName) => {
  const { state, actions } = useVRPUI();
  
  return {
    selectedIds: state.selections[entityType],
    selectItem: (id: number) => actions.selectItem(entityType, id),
    deselectItem: (id: number) => actions.deselectItem(entityType, id),
    selectAll: (ids: number[]) => actions.selectAll(entityType, ids),
    clearSelection: () => actions.clearSelection(entityType),
    toggleSelection: (id: number) => actions.toggleSelection(entityType, id)
  };
};

export const useFeatureUI = (feature: VRPFeature) => {
  const { state, actions } = useVRPUI();
  
  return {
    featureState: state.features[feature],
    setViewMode: (mode: ViewMode) => actions.setViewMode(feature, mode),
    setSorting: (sortBy: string, direction: 'asc' | 'desc') => actions.setSorting(feature, sortBy, direction),
    setFilters: (filters: Record<string, any>) => actions.setFilters(feature, filters),
    setSearchQuery: (query: string) => actions.setSearchQuery(feature, query),
    setPagination: (page: number, limit: number) => actions.setPagination(feature, page, limit),
    updateColumns: (columns: ColumnConfig[]) => actions.updateColumns(feature, columns),
    toggleRowExpansion: (id: number) => actions.toggleRowExpansion(feature, id),
    setSelectedTab: (tab: number) => actions.setSelectedTab(feature, tab)
  };
};

export const useModals = () => {
  const { state, actions } = useVRPUI();
  
  return {
    modals: state.modals,
    openModal: actions.openModal,
    closeModal: actions.closeModal,
    closeAllModals: actions.closeAllModals,
    isModalOpen: (modalType: string) => {
      if (modalType.startsWith('create-')) {
        return state.modals.createEntity.isOpen;
      }
      if (modalType.startsWith('edit-')) {
        return state.modals.editEntity.isOpen;
      }
      if (modalType.startsWith('delete-')) {
        return state.modals.deleteConfirmation.isOpen;
      }
      if (modalType.startsWith('bulk-')) {
        return state.modals.bulkActions.isOpen;
      }
      return state.modals.custom[modalType]?.isOpen || false;
    }
  };
};